import { Component, OnInit, computed, input, signal, viewChild } from '@angular/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { dispatch, select } from '@store/injectables';
import { retrieveRegions, selectRegionsViewModel } from '@store/scenario/regions/regions-slice';
import { Source } from '@scenario/models/source.model';
import { PsfUser } from '@scenario/models/psf-user.model';
import { Zone } from '@scenario/models/zone.model';
import { AnalystAddition, addAnalyst } from '@store/scenario/analyst/analyst-slice';
import {
  ButtonComponent,
  ModalActionsDirective,
  ModalComponent,
  ModalTitleDirective,
} from '@totalenergiescode/tds-angular';

@Component({
  selector: 'app-analyst-form-modal',
  templateUrl: './analyst-form-modal.component.html',
  styleUrl: './analyst-form-modal.component.scss',
  imports: [
    ButtonComponent,
    ModalActionsDirective,
    ModalComponent,
    ModalTitleDirective,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
  ],
})
export class AnalystFormModalComponent implements OnInit {
  private readonly dispatch = dispatch();
  public readonly modal = viewChild.required(ModalComponent);
  public readonly source = input.required<Source>();
  public readonly users = input.required<PsfUser[]>();
  public readonly regionsViewModel = select(selectRegionsViewModel);
  public readonly selectedUserId = signal<number | undefined>(undefined);
  public readonly selectedZoneIds = signal<number[]>([]);
  public readonly firstName = signal('');
  public readonly lastName = signal('');
  public readonly submitted = signal(false);
  public readonly userError = computed(() => this.submitted() && this.selectedUserId() === undefined);

  ngOnInit(): void {
    this.dispatch(retrieveRegions());
  }

  userLabel(user: PsfUser): string {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return name ? `${user.email} — ${name}` : user.email;
  }

  zoneLabel(zone: Zone): string {
    return `${zone.name} - ${zone.alias}`;
  }

  onUserChange(userId: number | undefined): void {
    this.selectedUserId.set(userId);
  }

  onZonesChange(zoneIds: number[]): void {
    this.selectedZoneIds.set(zoneIds ?? []);
  }

  onFirstNameChange(event: Event): void {
    this.firstName.set((event.target as HTMLInputElement).value);
  }

  onLastNameChange(event: Event): void {
    this.lastName.set((event.target as HTMLInputElement).value);
  }

  save(): void {
    this.submitted.set(true);
    if (this.userError()) {
      return;
    }

    const userId = this.selectedUserId();
    if (userId === undefined) {
      return;
    }

    const payload: AnalystAddition = {
      userId,
      sourceId: this.source().id,
      firstName: this.firstName().trim() || undefined,
      lastName: this.lastName().trim() || undefined,
      zoneIds: this.selectedZoneIds(),
    };

    this.dispatch(addAnalyst(payload));
    this.modal().closeModal('saved');
  }

  close(): void {
    this.modal().closeModal('cancel');
  }
}
